import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";

// Build 100f — Bookmarklet-Fix per route-bound BroadcastChannel-Handshake.
// Nur Moderator-Routen (/mod und /mod/...) dürfen eine laufende Moderator-
// Session von einem bereits offenen Moderator-Tab übernehmen oder selbst auf
// solche Anfragen antworten. Die Grenze wird auch bei clientseitiger SPA-
// Navigation erneut geprüft. Öffentliche Routen nehmen niemals am Handoff teil
// und entfernen vorsorglich eventuell noch vorhandene Moderator-Credentials
// aus ihrem eigenen sessionStorage.
const HANDSHAKE_TIMEOUT_MS = 250;
const CHANNEL_NAME = "vidalv-mod-session";

let sessionResponderChannel = null;
let routeBoundaryObserverInstalled = false;

function isModeratorRoute() {
  const path = window.location?.pathname || "";
  return path === "/mod" || path.startsWith("/mod/");
}

function clearPublicTabModSession() {
  try {
    sessionStorage.removeItem("mod_token");
    sessionStorage.removeItem("mod_user");
  } catch (_e) { /* ignore */ }
}

function closeSessionResponder() {
  if (!sessionResponderChannel) return;
  try { sessionResponderChannel.close(); } catch (_e) { /* ignore */ }
  sessionResponderChannel = null;
}

function installSessionResponder() {
  // Ausschließlich Moderator-Tabs dürfen einen Responder besitzen.
  if (!isModeratorRoute()) return;
  if (typeof BroadcastChannel === "undefined" || sessionResponderChannel) return;
  try {
    const bc = new BroadcastChannel(CHANNEL_NAME);
    sessionResponderChannel = bc;
    bc.onmessage = (event) => {
      // Fail closed, falls sich die Route zwischen zwei Events geändert hat.
      if (!isModeratorRoute()) {
        closeSessionResponder();
        clearPublicTabModSession();
        return;
      }
      const msg = event.data || {};
      if (msg.type === "request" && sessionStorage.getItem("mod_token")) {
        try {
          bc.postMessage({
            type: "offer",
            nonce: msg.nonce,
            token: sessionStorage.getItem("mod_token"),
            user: sessionStorage.getItem("mod_user"),
          });
        } catch (_e) { /* ignore */ }
      }
    };
  } catch (_e) {
    sessionResponderChannel = null;
  }
}

function enforceRouteSecurityBoundary() {
  if (isModeratorRoute()) {
    if (sessionStorage.getItem("mod_token")) installSessionResponder();
    return;
  }
  closeSessionResponder();
  clearPublicTabModSession();
}

function installRouteBoundaryObserver() {
  if (routeBoundaryObserverInstalled) return;
  routeBoundaryObserverInstalled = true;

  // BrowserRouter navigiert über history.pushState/replaceState; Back/Forward
  // erzeugt popstate. Nach jeder dieser Änderungen wird die Moderator-Grenze
  // synchron neu ausgewertet, bevor der Tab weiter als öffentliche Seite läuft.
  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = (...args) => {
    const result = originalPushState(...args);
    enforceRouteSecurityBoundary();
    return result;
  };
  window.history.replaceState = (...args) => {
    const result = originalReplaceState(...args);
    enforceRouteSecurityBoundary();
    return result;
  };
  window.addEventListener("popstate", enforceRouteSecurityBoundary);
}

function requestSessionFromPeers() {
  return new Promise((resolve) => {
    if (!isModeratorRoute() || typeof BroadcastChannel === "undefined") { resolve(false); return; }
    let bc;
    try { bc = new BroadcastChannel(CHANNEL_NAME); } catch (_e) { resolve(false); return; }
    const nonce = Math.random().toString(36).slice(2) + Date.now().toString(36);
    let done = false;
    const finish = (adopted) => {
      if (done) return;
      done = true;
      try { bc.close(); } catch (_e) {}
      resolve(adopted);
    };
    bc.onmessage = (event) => {
      if (!isModeratorRoute()) { finish(false); return; }
      const msg = event.data || {};
      if (msg.type === "offer" && msg.nonce === nonce && msg.token) {
        try {
          sessionStorage.setItem("mod_token", msg.token);
          if (msg.user) sessionStorage.setItem("mod_user", msg.user);
        } catch (_e) {}
        finish(true);
      }
    };
    try { bc.postMessage({ type: "request", nonce }); } catch (_e) { finish(false); return; }
    setTimeout(() => finish(false), HANDSHAKE_TIMEOUT_MS);
  });
}

function mountReact() {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

async function bootstrap() {
  installRouteBoundaryObserver();

  // Sicherheitsgrenze: öffentliche Seiten dürfen weder Moderator-Credentials
  // übernehmen noch weiterreichen. Alte, vor diesem Fix übernommene Werte
  // werden beim nächsten Laden oder SPA-Wechsel auf eine öffentliche Route
  // aus diesem Tab entfernt und der Responder wird geschlossen.
  if (!isModeratorRoute()) {
    enforceRouteSecurityBoundary();
    mountReact();
    return;
  }

  // Moderator-Tab mit eigener Session: Responder installieren und sofort mounten.
  if (sessionStorage.getItem("mod_token")) {
    installSessionResponder();
    mountReact();
    return;
  }

  // Neuer Moderator-Tab: kurz nach vorhandenen Moderator-Peers fragen, dann mounten.
  await requestSessionFromPeers();
  if (isModeratorRoute()) installSessionResponder();
  else enforceRouteSecurityBoundary();
  mountReact();
}

bootstrap();
